"use client";

import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-text-style/color";
import FontFamily from "@tiptap/extension-font-family";
import { ListItemWithMarkerColor } from "./listItemWithMarkerColor";
import { StoredSelectionHighlight } from "./storedSelectionHighlight";
import { TextDirection } from "./textDirection";
import { sceneBlockExtensions, sceneBlockFieldNodeNames } from "../../blocks";

export type DefaultAlignment =
    | "start"
    | "end"
    | "left"
    | "center"
    | "right"
    | "justify";

/** Configure StarterKit: heading levels 1–6, lists; disable blockquote, codeBlock, horizontalRule.
 * TrailingNode: only notAfter paragraph (so we DO add a trailing paragraph after a heading),
 * allowing Enter at end of a heading to create a new line. */
const starterKit = StarterKit.configure({
    heading: { levels: [1, 2, 3, 4, 5, 6] },
    bulletList: { keepMarks: true, keepAttributes: true },
    orderedList: { keepMarks: true, keepAttributes: true },
    listItem: false,
    blockquote: false,
    codeBlock: false,
    horizontalRule: false,
    trailingNode: { notAfter: ["paragraph", "heading"] },
});

/** Build extensions with the given default alignment (used for new paragraphs so styles stay consistent). */
export function getRichTextEditorExtensions(
    defaultAlignment: DefaultAlignment = "center"
) {
    return [
        ListItemWithMarkerColor,
        starterKit,
        Underline,
        TextAlign.configure({
            // Paragraph/heading are the StarterKit defaults; block-field types
            // come from the central scene-block registry so a new custom block
            // gets alignment support automatically.
            types: ["heading", "paragraph", ...sceneBlockFieldNodeNames],
            alignments: ["start", "end", "left", "center", "right", "justify"],
            defaultAlignment,
        }),
        TextStyle,
        Color,
        FontFamily.configure({ types: ["textStyle"] }),
        StoredSelectionHighlight,
        TextDirection,
        ...sceneBlockExtensions,
    ];
}

/** Extensions for the rich text editor (default: center alignment for new paragraphs). */
export const richTextEditorExtensions = getRichTextEditorExtensions("center");

export { TextDirection };
